// apps/client/src/ui/screens/title.tsx (title screen, 2026-10-05) — the first thing on every launch, at any path, signed in or not, over
// the slowly turning showcase town (title/backdrop.ts): the wordmark, the lead line, what this is in one sentence, and Play now, the first
// and biggest button. "What is this?" scrolls down to the short version. Play now unlocks audio inside the tap (the title theme starts),
// and swoops the camera down into the town; App then shows sign-in, the towns list or the invite landing (ui/router.ts baseScreen).
// Full screen (2026-10-15): Play now also asks a browser with element full screen (Android, desktop, an iPad) for it, and a tip says how
// to install where that is the way to full screen (ui/install.tsx).
// Portrait landing (2026-10-18): the title is the one screen that works upright, laid out for a tall phone (the words at the top, the
// town between, Play now at the foot in thumb reach). Play now upright waits for the phone to turn sideways under the rotate card
// (ui/rotate.tsx); turning it starts the swoop.
import { useEffect, useRef } from 'react';
import { setMusicOn, unlockAudio } from '../../audio/music';
import { enterFullscreen } from '../../boot/install';
import { titlePlan } from '../../state/slices/title';
import { useGame } from '../../state/store';
import { Music } from '../hud/Icons';
import { useT } from '../i18n';
import { InstallHint } from '../install';
import { Wordmark } from '../kit';
import { isPortrait, usePortrait } from '../rotate';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { title: undefined } }

// The wave is the brand board's hero edge (DESIGN.md Layout), here the top of the meadow-green band the short version sits on.
const WAVE = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 100' preserveAspectRatio='none'%3E%3Cpath d='M0 54 C 220 14 460 18 700 48 S 1180 86 1440 38 L1440 100 L0 100 Z' fill='%23000'/%3E%3C/svg%3E\") top / 100% 100% no-repeat";
const CSS = `
.title-screen { position: absolute; inset: 0; overflow: hidden; }
/* The soft loading state: the title's own sky, until the town behind it draws its first frame (or for good, without WebGL). */
.title-screen::before { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, var(--sky-300) 0%, var(--sky-200) 56%, var(--sky-100) 100%);
  transition: opacity .9s var(--ease-out); }
:root[data-backdrop='live'] .title-screen::before { opacity: 0; }
.title-scroll { position: absolute; inset: 0; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--wood-600) 55%, transparent) transparent;
  transition: opacity .5s var(--ease-out), filter .5s var(--ease-out), transform .5s var(--ease-out); }
.title-hero { position: relative; box-sizing: border-box; min-height: 100%; display: grid; align-content: center;
  padding: max(18px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right)) max(18px, env(safe-area-inset-bottom)) max(clamp(24px, 6cqw, 72px), env(safe-area-inset-left)); }
/* A soft cream haze on the left keeps the words easy to read over any time of day; the town shows clear on the right. */
.title-hero::before { content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(90deg, rgba(255,251,241,.94) 0%, rgba(255,251,241,.84) 28%, rgba(255,251,241,.42) 50%, rgba(255,251,241,0) 66%); }
/* On narrow phones the words reach further across the town, so the haze does too. */
@media (max-width: 800px) { .title-hero::before { background: linear-gradient(90deg, rgba(255,251,241,.94) 0%, rgba(255,251,241,.86) 36%, rgba(255,251,241,.56) 58%, rgba(255,251,241,0) 76%); } }
.title-copy { position: relative; display: grid; justify-items: start; max-width: min(50cqw, 560px); }
.title-copy .wordmark { font-size: clamp(54px, min(9.4cqw, 16cqh), 94px);   /* DESIGN.md display, sized to a landscape phone */ }
.wordmark-house { pointer-events: none; }
.title-lead { margin: clamp(6px, 2cqh, 14px) 0 0; font-weight: 900; font-size: clamp(26px, min(3.1cqw, 6.6cqh), 32px); line-height: 1.12;
  letter-spacing: -0.012em; color: var(--wood-800); text-wrap: balance; }
.title-blurb { margin: clamp(6px, 1.8cqh, 12px) 0 0; font-weight: 700; font-size: clamp(15px, min(1.9cqw, 4.3cqh), 17px); line-height: 1.45;
  color: var(--wood-700); max-width: 38ch; text-wrap: pretty; }
.title-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 14px; margin-top: clamp(14px, 4.6cqh, 30px); }
.title-play { font-size: 18px; padding: 17px 44px 18px; }   /* the Get variant's type in a roomier pill: the biggest control */
.title-more { display: inline-flex; align-items: center; gap: .4em; font-size: 17px; padding: 12px 16px 13px 18px; }
.title-more svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2.6; stroke-linecap: round; stroke-linejoin: round; }
.title-music { position: absolute; z-index: 2; top: max(14px, env(safe-area-inset-top)); right: max(16px, env(safe-area-inset-right));
  transition: opacity .4s var(--ease-out); }
.title-music[aria-pressed='false'] { color: var(--wood-600); }
.title-about { position: relative; background: var(--leaf-100); scroll-margin-top: 0;
  padding: clamp(18px, 5cqh, 40px) max(24px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left)); }
.title-about::before { content: ''; position: absolute; left: 0; right: 0; bottom: 100%; height: clamp(26px, 4cqw, 60px); background: var(--leaf-100);
  -webkit-mask: ${WAVE}; mask: ${WAVE}; }
.title-about-inner { max-width: 1160px; margin: 0 auto; display: grid; gap: clamp(14px, 3cqh, 22px); }
.title-about h2 { margin: 0; font-weight: 900; font-size: clamp(32px, min(3.8cqw, 8cqh), 52px); line-height: 1.04; letter-spacing: -0.02em; color: var(--wood-800); }
.title-cards { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; }
@media (max-width: 700px) { .title-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.title-card { background: var(--cream-50); border-radius: var(--r-panel); box-shadow: var(--sh-card); padding: 16px 16px 18px; display: grid; align-content: start; gap: 6px; }
.title-card svg { width: 52px; height: 52px; margin-bottom: 4px; }
.title-card h3 { margin: 0; font-weight: 900; font-size: 18px; line-height: 1.15; color: var(--wood-800); text-wrap: balance; }
.title-card p { margin: 0; font-weight: 600; font-size: 15px; line-height: 1.45; color: var(--wood-700); }
.title-foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px 20px; padding-top: 4px; }
.title-foot p { margin: 0; font-weight: 800; color: var(--wood-700); max-width: 60ch; text-wrap: pretty; }
/* Portrait landing (2026-10-18): a phone held upright gets the title laid out for a tall screen. The wordmark (beside the music switch),
   the lead line and the blurb at the top over a cream haze, the turning town in the band below them (title/camera.ts titleFrame), and
   Play now at the foot, wide and in thumb reach, with What is this? under it and the install tip last, so nothing ever covers Play now.
   The short version's cards stack in one column. */
@media (max-aspect-ratio: 1/1) {
  .title-hero { display: flex; flex-direction: column; align-content: normal;
    padding: max(14px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left)); }
  .title-hero::before { background: linear-gradient(180deg, rgba(255,251,241,.95) 0, rgba(255,251,241,.9) 236px, rgba(255,251,241,.5) 290px, rgba(255,251,241,0) 350px); }
  .title-copy { flex: 1 0 auto; display: flex; flex-direction: column; align-items: stretch; max-width: none; }
  .title-copy .wordmark { align-self: flex-start; font-size: clamp(42px, 13.4cqw, 66px); }
  .title-lead { margin-top: 10px; font-size: clamp(24px, 7.2cqw, 32px); max-width: 15em; }
  .title-blurb { margin-top: 8px; font-size: clamp(15px, 4.2cqw, 18px); max-width: 34ch; }
  .title-actions { margin-top: auto; padding-top: 20px; flex-direction: column; flex-wrap: nowrap; align-items: center; gap: 8px; }
  .title-actions .title-play { width: 100%; max-width: 440px; font-size: 20px; padding: 19px 24px 20px; }
  .title-hero .install-hint { position: relative; right: auto; bottom: auto; align-self: center; width: 100%; max-width: 440px; margin-top: 12px; }
  .title-about { padding-top: 22px; }
  .title-about h2 { font-size: clamp(30px, 9cqw, 42px); }
  .title-cards { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  .title-card { grid-template-columns: 52px minmax(0, 1fr); column-gap: 14px; }
  .title-card svg { grid-row: span 2; margin-bottom: 0; }
  .title-foot { flex-direction: column; align-items: stretch; }
  .title-foot .title-play { align-self: center; width: 100%; max-width: 440px; }
}
/* Play now: the words dissolve as the camera dives into the town. With reduced motion (the view fades rather than swoops) they only fade:
   no blur, no drop, and the fade is kept although tokens.css stops every other transition. */
.title-screen[data-step='swoop'], .title-screen[data-step='turn'] { pointer-events: none; }
.title-screen[data-step='swoop'] .title-scroll { opacity: 0; }
/* Play now upright: the words step aside for the rotate card (and stay out of sight as the phone turns and the swoop begins). */
.title-screen[data-step='turn'] .title-scroll { opacity: 0; }
.title-screen[data-step='swoop'][data-motion='swoop'] .title-scroll { filter: blur(6px); transform: translateY(10px); }
.title-screen[data-motion='fade'] .title-scroll { transition: opacity .5s var(--ease-out) !important; }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'title';
  el.textContent = CSS;
  document.head.appendChild(el);
}
const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// The short version's pictures: flat shapes on a 64-unit grid in the world palette (DESIGN.md "Icons").
const icon = { viewBox: '0 0 64 64', 'aria-hidden': true as const, focusable: 'false' as const };
// World-palette keys with no CSS token: house-wall, trunk and dye-sky from DESIGN.md, and the tent's shaded side (sun-400 in shadow).
const WALL = '#FFF4E2', KEY = 'var(--cream-300)', TRUNK = '#8A6A4F', GRASS = 'var(--leaf-400)', DYE_SKY = '#5AA9E6', TENT_SHADE = '#C8902A';
function Neighbours() {
  return (
    <svg {...icon}>
      <rect x="3" y="50" width="58" height="6" rx="3" fill={GRASS} />
      <rect x="7" y="31" width="22" height="20" rx="3" fill={WALL} stroke={KEY} strokeWidth="1.5" />
      <path d="M5 33 18 21l13 12" fill="none" stroke="var(--coral-500)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="15" y="39" width="7" height="12" rx="2" fill={TRUNK} />
      <rect x="35" y="35" width="22" height="16" rx="3" fill={WALL} stroke={KEY} strokeWidth="1.5" />
      <path d="M33 37 46 26l13 11" fill="none" stroke={DYE_SKY} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="43" y="42" width="7" height="9" rx="2" fill={TRUNK} />
    </svg>
  );
}
function Tent() {
  return (
    <svg {...icon}>
      <rect x="3" y="50" width="58" height="6" rx="3" fill={GRASS} />
      <path d="M10 50 32 17l22 33z" fill="var(--sun-400)" stroke="var(--sun-400)" strokeWidth="5" strokeLinejoin="round" />
      <path d="M32 26 25.5 50h13z" fill={TENT_SHADE} />
      <path d="M32 17V7l8 3.2-8 3.3" fill="var(--flag-500)" stroke="var(--flag-500)" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}
function Fish() {
  return (
    <svg {...icon}>
      <path d="M45 32 58 22v20z" fill={DYE_SKY} stroke={DYE_SKY} strokeWidth="4" strokeLinejoin="round" />
      <ellipse cx="29" cy="32" rx="19" ry="12" fill={DYE_SKY} />
      <ellipse cx="30" cy="36.5" rx="12" ry="4.5" fill="var(--sky-300)" />
      <path d="M24 21.5c4-4.5 11-5 15-2" fill="none" stroke="var(--sky-700)" strokeWidth="3" strokeLinecap="round" />
      <circle cx="19" cy="29" r="2.8" fill="var(--wood-900)" />
    </svg>
  );
}
function Letter() {
  return (
    <svg {...icon}>
      <rect x="7" y="15" width="50" height="35" rx="5" fill={WALL} stroke={KEY} strokeWidth="2" />
      <path d="M9.5 18.5 32 35l22.5-16.5" fill="none" stroke={KEY} strokeWidth="2.6" strokeLinejoin="round" />
      <circle cx="32" cy="35" r="6.5" fill="var(--flag-500)" />
    </svg>
  );
}
const CARDS = [['friends', Neighbours], ['house', Tent], ['finds', Fish], ['post', Letter]] as const;

function Title() {
  injectStyle();
  const t = useT();
  const step = useGame((s) => s.titleStep);
  const motion = useGame((s) => s.titleMotion);
  const musicOn = useGame((s) => s.settings.music > 0);
  const scroll = useRef<HTMLDivElement>(null), about = useRef<HTMLElement>(null);
  const portrait = usePortrait();
  const play = () => {
    unlockAudio();                                                // inside the tap: browsers start audio only from a gesture
    enterFullscreen();                                            // …and go full screen only from one (not on an iPhone; silent if refused)
    scroll.current?.scrollTo?.({ top: 0 });
    if (isPortrait()) useGame.getState().waitForLandscape();      // upright: the rotate card, then the swoop once the phone turns
    else useGame.getState().playNow(titlePlan(reducedMotion()), performance.now());
  };
  useEffect(() => {
    if (step === 'turn' && !portrait) useGame.getState().playNow(titlePlan(reducedMotion()), performance.now());
  }, [step, portrait]);
  const playButton = (extra = '') => (
    <button type="button" className={`btn btn-primary title-play ${extra}`} onClick={play}>{t('title.play')}</button>
  );
  return (
    <div className="title-screen hit" data-step={step} data-motion={motion}>
      <div className="title-scroll" ref={scroll}>
        <section className="title-hero">
          <div className="title-copy">
            <Wordmark />
            <p className="title-lead">{t('title.lead')}</p>
            <p className="title-blurb">{t('title.blurb')}</p>
            <div className="title-actions">
              {playButton()}
              <button type="button" className="btn btn-quiet title-more"
                onClick={() => about.current?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' })}>
                {t('title.more')}
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6 9.5 6 6 6-6" /></svg>
              </button>
            </div>
          </div>
          {/* After Play now in the DOM, so Play now stays the first button; it scrolls away with the hero. */}
          <button type="button" className="round-btn title-music" aria-label={t('title.music')} aria-pressed={musicOn}
            title={musicOn ? t('title.musicOn') : t('title.musicOff')} onClick={() => setMusicOn(!musicOn)}>
            <Music off={!musicOn} />
          </button>
          <InstallHint />
        </section>
        <section className="title-about" ref={about} aria-labelledby="title-about-h">
          <div className="title-about-inner">
            <h2 id="title-about-h">{t('title.aboutTitle')}</h2>
            <ul className="title-cards">
              {CARDS.map(([k, Pic]) => (
                <li key={k} className="title-card">
                  <Pic />
                  <h3>{t(`title.${k}Title`)}</h3>
                  <p>{t(`title.${k}Body`)}</p>
                </li>
              ))}
            </ul>
            <div className="title-foot">
              <p>{t('title.inviteOnly')}</p>
              {playButton('title-play-again')}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
export const screen: ScreenDef<'title'> = { id: 'title', layer: 'full', component: Title };
